import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { DefinitionControllerService } from '@rapider/sdk'; // Adjust import based on your SDK path
import { NotificationService } from '@rapider/services';
import { ONBOARDING_CONFIG, buildTemplatesFilter } from '../onboarding.config';
import { Router } from '@angular/router';

/**
 * @class DataTemplatesComponent
 * @description Renders a gallery of data model templates. Implements server-side search, pagination, and excludes heavy schema data from the initial load.
 */
@Component({
  selector: 'rapider-data-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzInputModule,
    NzTagModule,
    NzTooltipModule,
  ],
  templateUrl: './data-templates.component.html',
  host: {
    class:
      'block h-full w-full bg-body-background overflow-hidden flex flex-col min-h-0',
  },
})
export class DataTemplatesComponent implements OnInit, OnDestroy {
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);

  public readonly config = ONBOARDING_CONFIG;

  // Data State
  public localTemplates = signal<any[]>([]);

  // UI State
  public isLoading = signal<boolean>(true);
  public isPaginating = signal<boolean>(false);
  public hasMoreTemplates = signal<boolean>(false);

  // Toolbar State
  public searchQuery = signal<string>('');

  private readonly pageSize = 20;
  private searchSubject = new Subject<string>();
  private searchSubscription!: Subscription;

  constructor() {
    // Prevent API spam during active typing by debouncing the input
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.resetAndFetchTemplates();
      });
  }

  ngOnInit(): void {
    this.fetchTemplates(true);
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Triggers a fresh fetch from page 0.
   */
  public resetAndFetchTemplates(): void {
    this.fetchTemplates(true);
  }

  /**
   * Fetches lightweight template definitions from the API.
   * @param {boolean} reset - If true, replaces existing list. If false, appends to the list (Load More).
   */
  public fetchTemplates(reset: boolean = false): void {
    if (reset) {
      this.isLoading.set(true);
      this.localTemplates.set([]);
    } else {
      this.isPaginating.set(true);
    }

    const currentSkip = reset ? 0 : this.localTemplates().length;
    const filter = buildTemplatesFilter(
      this.pageSize,
      currentSkip,
      this.searchQuery(),
    );

    // Call the specific endpoint designated for public definitions
    this.definitionController.getPublicDefinitions({ filter }).subscribe({
      next: (res: any[]) => {
        if (reset) {
          this.localTemplates.set(res);
        } else {
          this.localTemplates.update((prev) => [...prev, ...res]);
        }

        // Determine if more templates exist based on the response chunk size
        this.hasMoreTemplates.set(res.length === this.pageSize);

        this.isLoading.set(false);
        this.isPaginating.set(false);
      },
      error: () => {
        this.notificationService.createNotification(
          'error',
          'Error',
          this.config.notifications.fetchFailed,
        );
        this.isLoading.set(false);
        this.isPaginating.set(false);
      },
    });
  }

  /**
   * Emits the search string to the subject for debouncing.
   * @param {string} value - The input search string.
   */
  public onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  /**
   * Handles the preview action for a specific template.
   * @param {any} template - The selected template object to preview.
   */
  public previewTemplate(template: any): void {
    // To be implemented: Navigate to setup wizard/preview page with the selected template ID
    console.log('Previewing Template:', template);
    this.router.navigate(['/data-templates', template.id]);
  }
}
